import { useEffect, useRef } from 'react';
import { CUSTOM_EVENTS } from '~/common/constants/events';
import { CookieService } from '~/common/utils/cookies';
import {
  clearKlaviyoStorage,
  isKlaviyoScriptLoaded,
  loadKlaviyoScript,
} from '~/common/utils/klaviyo';

/**
 * Long enough for `CookiePreferencesPanel`'s 0.2s close tween to finish. Withdrawal is
 * dispatched from inside the save click, so reloading synchronously would rip the
 * document away mid-animation, with the panel still on screen, at the exact moment the
 * visitor exercises a privacy right.
 */
const WITHDRAWAL_RELOAD_DELAY_MS = 300;

/**
 * Loads Klaviyo's onsite script once marketing consent exists, and on withdrawal purges
 * Klaviyo's client-side storage and reloads the document — the only way to stop an
 * already-running klaviyo.js, which has no supported unload.
 *
 * Shaped as a sibling of `useGTMConsent` rather than merged into it: merging would
 * put GTM's Consent Mode v2 signalling and Klaviyo's script-append lifecycle on one
 * code path, coupling two vendors' behaviour for no saving.
 *
 * Marketing rather than analytics, deliberately. Klaviyo's cookie links browsing to a
 * named person for direct marketing — profiling, not aggregate measurement — so
 * loading it on an analytics-only consent would not be defensible for a UK/EU brand.
 * The cost is real and expected: visitors who accept analytics but decline marketing
 * produce no Klaviyo data, so these metrics will read well below their GA4 equivalents.
 */
export const useKlaviyoConsent = (companyId: string | undefined) => {
  const reloadTimerRef = useRef<number | null>(null);

  useEffect(() => {
    const syncConsentToKlaviyo = () => {
      // No decision recorded yet — the banner is still up. Do nothing, and in
      // particular do NOT clear: React flushes child effects before parent ones, so
      // the PDP view effect has already queued the visitor's first pageview by the
      // time this runs. Clearing here would discard exactly the event this design
      // exists to capture, on every cold load before consent.
      if (CookieService.getConsent() === null) return;

      if (!CookieService.hasMarketingConsent()) {
        // A decision exists and it is not consent: rejected, withdrawn in the
        // preferences panel, or lapsed past the TTL.
        clearKlaviyoStorage();

        // Clearing storage is not withdrawal on its own. klaviyo.js keeps its own timers
        // — the `Active on Site` beacon among them — and rewrites `__kla_id` within
        // seconds of any purge, and there is no supported way to unload a third-party SDK
        // in place. So the document is reloaded, after the purge so the reload cannot race
        // a rewrite.
        //
        // The script-presence check is what makes that safe in both directions. A purge on
        // a page that never loaded klaviyo.js does not reload the document underneath the
        // visitor — and it is why this cannot loop: `loadKlaviyoScript` re-checks consent
        // before appending, so on any fresh document the script is absent and the next
        // purge stops here.
        //
        // A lapsed TTL cannot produce a surprise reload either. `setConsent` writes
        // `maxAge` and `ts` from the same instant and every caller passes a fresh `ts`, so
        // the cookie expires exactly when `isConsentExpired` starts returning true; there
        // is no window in which a stale-but-present decision reads as a withdrawal.
        if (!isKlaviyoScriptLoaded()) return;

        reloadTimerRef.current = window.setTimeout(
          () => window.location.reload(),
          WITHDRAWAL_RELOAD_DELAY_MS,
        );
        return;
      }

      // Deferred out of the hydration commit. klaviyo.js plus its secondary bundles is
      // tens of ms of main-thread work on a mid-tier mobile, and appending it here
      // would put that in the most contended window on the page. Nothing is lost by
      // waiting: events queue in `_klOnsite` and are drained when it loads.
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(() => loadKlaviyoScript(companyId));
        return;
      }

      // Safari has no requestIdleCallback.
      window.setTimeout(() => loadKlaviyoScript(companyId), 0);
    };

    syncConsentToKlaviyo();

    window.addEventListener(
      CUSTOM_EVENTS.cookieConsentUpdated,
      syncConsentToKlaviyo,
    );

    return () => {
      window.removeEventListener(
        CUSTOM_EVENTS.cookieConsentUpdated,
        syncConsentToKlaviyo,
      );

      if (reloadTimerRef.current !== null) {
        window.clearTimeout(reloadTimerRef.current);
      }
    };
  }, [companyId]);
};
